import { CheckCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";
import { useRefonte } from "../translations/refonte";
import { useHome } from "../translations/home";
import { DemoForm } from "./DemoForm";

const LOGO_URL = "/cayus-logo.png";

const Footer = ({ withDemoForm = true }) => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const n = useRefonte(language).nav;
  const home = useHome(language);

  return (
    <footer className="footer" data-testid="footer">
      {/* Section formulaire de démo - masquée sur la home, qui a son propre CTA final */}
      {withDemoForm && (
        <section id="demo-form" className="scroll-mt-20 py-20 border-b border-slate-800" data-testid="demo-form-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <span className="mono-cayus text-[#5EB3F6] mb-4 block">{t.footer.tagline}</span>
                <h2 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight mb-6 break-words">
                  {home.final.title}
                </h2>
                <p className="text-base sm:text-lg text-[#9098A3] leading-relaxed mb-8">{home.hero.sub}</p>
                <div className="space-y-4 text-[#9098A3] overflow-hidden">
                  {home.wall.guarantees.map((g, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#34D399] flex-shrink-0 mt-0.5" />
                      <span className="break-words">{g.t}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/[0.06] border border-white/15 rounded-2xl p-8">
                <DemoForm />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final Message Section */}
      {t.finalMessage && (
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-2xl sm:text-3xl text-[#E4EEFC] mb-2">
              {t.finalMessage.line1}
            </p>
            <p className="text-2xl sm:text-3xl text-[#E4EEFC] mb-4">
              {t.finalMessage.line2}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-[#60A5FA]">
              {t.finalMessage.highlight}
            </p>
          </div>
        </section>
      )}

      {/* Footer Bottom - colonnes */}
      <div className="py-12 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full opacity-[0.07] pointer-events-none">
          <svg viewBox="0 0 1200 120" fill="none" preserveAspectRatio="none" className="w-full h-24">
            <path d="M0 62 C 90 62, 130 24, 210 34 S 360 98, 460 72 S 620 14, 740 42 S 930 92, 1040 60 S 1150 44, 1200 56" stroke="#0047AB" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
            <div>
              <img src={LOGO_URL} alt="Cayus AI" className="h-8 w-auto brightness-0 invert mb-4" />
              <p className="text-sm text-[#9098A3] leading-relaxed">{n.tagline}</p>
            </div>
            <div>
              <p className="mono-cayus text-[10px] text-[#5E6C83] mb-4">{n.product}</p>
              <ul className="flex flex-col gap-2.5 text-sm text-[#9098A3]">
                <li><a href="/produit/managers" className="hover:text-white transition-colors">{n.managers}</a></li>
                <li><a href="/produit/senior-managers" className="hover:text-white transition-colors">{n.senior}</a></li>
                <li><a href="/produit/rh" className="hover:text-white transition-colors">{n.hr}</a></li>
                <li><a href="/produit/comite-executif" className="hover:text-white transition-colors">{n.exco}</a></li>
              </ul>
            </div>
            <div>
              <p className="mono-cayus text-[10px] text-[#5E6C83] mb-4">{n.resources}</p>
              <ul className="flex flex-col gap-2.5 text-sm text-[#9098A3]">
                <li><a href="/methodologie" className="hover:text-white transition-colors">{n.methodology}</a></li>
                <li><a href="/programme-pilote" className="hover:text-white transition-colors" data-testid="footer-pilot-link">{n.pilotProgram}</a></li>
                <li><a href="/securite" className="hover:text-white transition-colors">{n.security}</a></li>
                <li><a href="/pourquoi-cayus" className="hover:text-white transition-colors">{n.why}</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <p className="mono-cayus text-[10px] text-[#5E6C83] mb-4">{n.company}</p>
              <ul className="flex flex-col gap-2.5 text-sm text-[#9098A3]">
                <li><a href="/contact" className="hover:text-white transition-colors">{t.footer.links.contact}</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">{t.footer.links.privacy}</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">{t.footer.links.terms}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-[#5E6C83]">© Cayus Ltd {new Date().getFullYear()} · {n.legal}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
